import { Image, Paperclip, Plus } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import type { DocumentPickerAsset } from "expo-document-picker";
import * as DocumentPicker from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import * as ImagePicker from "expo-image-picker";
import { MediaTypeOptions } from "expo-image-picker";
import { useEffect, useId, useState } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { type GetProps, Popover, YStack } from "tamagui";

import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";
import { useAuthState } from "@/hooks/useAuthState";
import { captureException } from "@/sentry";
import { useConversationComposition } from "@/store/conversation";
import { getShadow } from "@/theme/helpers";
import {
  ANONYMOUS_USER_ATTACHMENT_LIMIT,
  exceedsAnonymousAttachmentLimit,
  getAssetFilename,
} from "@/utils/attachments";
import { Separator } from "./Layout";

// Same as allowed types in Chat service schema
export const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

export const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  ...ALLOWED_IMAGE_MIME_TYPES,
  "application/pdf",
]);

const UNSUPPORTED_IMAGE_MESSAGE =
  "You have selected an image with a format we don't support. Please try again with a supported image format (JPEG, PNG, or WebP), or use the document option to attach a PDF.";

interface AttachmentsButtonProps {
  disabled?: boolean;
  isUploading?: boolean;
  onAttachmentsPicked: (
    assets: (DocumentPickerAsset | ImagePickerAsset)[],
    files: File[],
  ) => void;
  conversationType?: string | null;
  trigger?: React.ReactNode;
  triggerProps?: GetProps<typeof Button.Wrapper>;
}

interface AttachmentMenuItemProps {
  icon: React.ElementType;
  text: string;
  onPress: () => void;
  closeMenu: () => void;
}

const AttachmentMenuItem = ({
  icon: Icon,
  text,
  onPress,
  closeMenu,
}: AttachmentMenuItemProps) => {
  const handlePress = () => {
    closeMenu();
    onPress();
  };

  return (
    <Button.Wrapper
      gap="$100"
      padding="$75"
      alignItems="center"
      onPress={handlePress}
      cursor="pointer"
      borderRadius="$100"
      hoverStyle={{
        backgroundColor: "$navHover",
      }}
    >
      <Icon size="$225" color="$iconBase" flexShrink={0} />
      <YStack flexShrink={0}>
        <Text size="$xs" whiteSpace="nowrap">
          {text}
        </Text>
      </YStack>
    </Button.Wrapper>
  );
};

const AttachmentsButton = ({
  disabled,
  isUploading,
  onAttachmentsPicked,
  conversationType,
  trigger,
  triggerProps,
}: AttachmentsButtonProps) => {
  const menuId = useId();
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const rotation = useSharedValue(0);
  const { isSignedIn } = useAuthState();
  const { attachmentCount, attachmentSize } = useConversationComposition();
  const toast = useToastController();

  useEffect(() => {
    rotation.value = withTiming(attachmentMenuOpen ? 45 : 0, {
      duration: 200,
    });
  }, [attachmentMenuOpen, rotation]);

  const animatedIconStyle = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${rotation.value}deg` }],
    };
  }, [rotation]);

  const isCuraiConversation = conversationType === "curai";
  // Higher limit for clinic conversations. Keep in sync with the Chat service's
  // CURAI_CONVERSATION_ATTACHMENT_SIZE_LIMIT_BYTES.
  const uploadLimitBytes = isCuraiConversation
    ? 256 * 1024 * 1024 // 256MB
    : 50 * 1024 * 1024; // 50MB
  const uploadLimitLabel = isCuraiConversation ? "256MB" : "50MB";
  const imageButtonText = isCuraiConversation
    ? "Send an image"
    : "Upload an image";
  const documentButtonText = isCuraiConversation
    ? "Send a document"
    : "Upload a document";

  const processAndUploadFiles = async (
    assets: (DocumentPickerAsset | ImagePickerAsset)[],
  ) => {
    if (
      exceedsAnonymousAttachmentLimit(
        isSignedIn,
        attachmentCount,
        assets.length,
      )
    ) {
      toast.show(
        `You can upload up to ${ANONYMOUS_USER_ATTACHMENT_LIMIT} attachments in this conversation before signing in.`,
        { duration: 10000 },
      );
      return;
    }

    const results = await Promise.all(
      assets.map(async (asset) => {
        try {
          const response = await fetch(asset.uri);
          const blob = await response.blob();
          const filename = getAssetFilename(asset);
          return {
            asset,
            file: new File([blob], filename, { type: blob.type }),
          };
        } catch (error) {
          captureException(error, {
            extra: {
              category: "attachments",
              assetUri: asset.uri,
              assetMimeType: asset.mimeType,
            },
          });
          return null;
        }
      }),
    );

    const successful = results.filter(
      (r): r is { asset: DocumentPickerAsset | ImagePickerAsset; file: File } =>
        r !== null,
    );

    if (successful.length < assets.length) {
      toast.show(
        "Some files could not be loaded. Please try selecting them again.",
        { duration: 10000 },
      );
    }

    if (successful.length === 0) {
      return;
    }

    const files = successful.map((r) => r.file);
    const successfulAssets = successful.map((r) => r.asset);

    const newAssetsSize = files.reduce((sum, file) => sum + file.size, 0);
    if (attachmentSize + newAssetsSize > uploadLimitBytes) {
      toast.show(
        `You have exceeded the attachment limit of ${uploadLimitLabel}.`,
        {
          duration: 10000,
        },
      );
      return;
    }

    onAttachmentsPicked(
      successfulAssets.map((asset, i) => ({
        ...asset,
        mimeType: files[i].type || asset.mimeType,
      })),
      files,
    );
  };

  const handlePickerResult = async (result: {
    canceled: boolean;
    assets: any[] | null;
  }) => {
    if (result.canceled || !result.assets) {
      return;
    }

    await processAndUploadFiles(result.assets);
  };

  const handleImagePick = async () => {
    let result: ImagePicker.ImagePickerResult;
    try {
      result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        selectionLimit: 0,
        quality: 1,
      });
    } catch {
      // On web the picker rejects when the file dialog's type filter is
      // bypassed and a non-media file (e.g. a PDF) is selected.
      toast.show(UNSUPPORTED_IMAGE_MESSAGE, { duration: 10000 });
      return;
    }

    if (result.canceled || !result.assets) {
      return;
    }

    const supportedAssets = result.assets.filter((asset) => {
      const mimeType = asset.mimeType?.toLowerCase();
      return mimeType ? ALLOWED_IMAGE_MIME_TYPES.has(mimeType) : false;
    });

    if (supportedAssets.length !== result.assets.length) {
      toast.show(UNSUPPORTED_IMAGE_MESSAGE, { duration: 10000 });
    }

    if (supportedAssets.length === 0) {
      return;
    }

    await processAndUploadFiles(supportedAssets);
  };

  const handleDocumentPick = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      multiple: true,
      type: "application/pdf",
      copyToCacheDirectory: true,
    });

    await handlePickerResult(result);
  };

  return (
    <Popover
      allowFlip
      placement="top-start"
      open={attachmentMenuOpen}
      onOpenChange={setAttachmentMenuOpen}
      offset={{ mainAxis: 6, crossAxis: -10 }}
    >
      <Popover.Trigger asChild>
        <Button.Wrapper
          padding="$50"
          marginVertical="$100"
          onPress={() => setAttachmentMenuOpen(!attachmentMenuOpen)}
          aria-label={
            attachmentMenuOpen ? "Close attachments menu" : "Attach files"
          }
          aria-expanded={attachmentMenuOpen}
          aria-controls={menuId}
          borderRadius="$100"
          backgroundColor={attachmentMenuOpen ? "$navSelected" : "transparent"}
          disabled={disabled || isUploading}
          opacity={disabled || isUploading ? 0.5 : 1}
          {...triggerProps}
        >
          {trigger || (
            <Animated.View style={animatedIconStyle}>
              <Plus size="$250" color="$iconBase" flexShrink={0} />
            </Animated.View>
          )}
        </Button.Wrapper>
      </Popover.Trigger>
      <Popover.Content
        id={menuId}
        enterStyle={{ y: 10, opacity: 0 }}
        exitStyle={{ y: 10, opacity: 0 }}
        transition={[
          "standard",
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
        padding="$75"
        backgroundColor="$fillCallout"
        borderRadius="$150"
        {...getShadow()}
        zIndex={999999} // sadly necessary to get the popover to appear in Curai chats on mobile
      >
        <YStack gap="$75">
          <AttachmentMenuItem
            icon={Image}
            text={imageButtonText}
            onPress={handleImagePick}
            closeMenu={() => setAttachmentMenuOpen(false)}
          />
          <Separator />
          <AttachmentMenuItem
            icon={Paperclip}
            text={documentButtonText}
            onPress={handleDocumentPick}
            closeMenu={() => setAttachmentMenuOpen(false)}
          />
        </YStack>
      </Popover.Content>
    </Popover>
  );
};

export default AttachmentsButton;
